import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Check,
  ChevronRight,
  ChevronLeft,
  Save,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";

/**
 * AddLotWizard - Step-by-step wizard for adding a new lot/property
 * 
 * Steps:
 * 1. Basic lot info (lot number, address, block/phase, acreage)
 * 2. Review and save
 * 
 * Note: GIS mapping can be added from the property detail page after creation
 */

const STEPS = [
  { id: 1, title: "Basic Info", icon: Building2, description: "Lot details" },
  { id: 2, title: "Review & Save", icon: Check, description: "Confirm" },
];

const AddLotWizard = ({ open, onOpenChange, onSuccess, token, owners = [], API }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    lot_number: "",
    address: "",
    block: "",
    phase: "",
    acreage: "",
    parcel_id: "",
    notes: "",
    owner_id: "",
  });

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.lot_number) {
        toast.error("Lot number is required");
        return false;
      }
      if (!formData.address) {
        toast.error("Address is required");
        return false;
      }
    }
    return true;
  };

  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(2);
    }
  };

  const goToPrevStep = () => {
    setCurrentStep(1);
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const propertyData = {
        lot_number: formData.lot_number,
        address: formData.address,
        block: formData.block || null,
        phase: formData.phase || null,
        acreage: formData.acreage ? parseFloat(formData.acreage) : null,
        parcel_id: formData.parcel_id || null,
        notes: formData.notes || null,
        owner_id: formData.owner_id || null,
      };

      await axios.post(
        `${API}/properties`,
        propertyData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Lot ${formData.lot_number} created successfully!`);
      
      // Reset form
      setFormData({
        lot_number: "",
        address: "",
        block: "",
        phase: "",
        acreage: "",
        parcel_id: "",
        notes: "",
        owner_id: "",
      });
      setCurrentStep(1);
      
      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
      
    } catch (error) {
      console.error("Error creating property:", error);
      toast.error(error.response?.data?.detail || "Failed to create property");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="add-lot-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Add New Lot
          </DialogTitle>
          <DialogDescription>
            Create a new property/lot. GIS mapping can be added from the property detail page.
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2 py-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center">
                <div
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors",
                    isActive && "bg-primary text-primary-foreground",
                    isCompleted && "bg-green-100 text-green-700",
                    !isActive && !isCompleted && "bg-muted text-muted-foreground"
                  )}
                >
                  <div
                    className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center",
                      isActive && "bg-primary-foreground/20",
                      isCompleted && "bg-green-200",
                      !isActive && !isCompleted && "bg-background"
                    )}
                  >
                    {isCompleted ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  </div>
                  <span className="text-sm font-medium">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground" />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="py-4">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lot Number *</Label>
                  <Input
                    value={formData.lot_number}
                    onChange={(e) => updateFormData("lot_number", e.target.value)}
                    placeholder="e.g., 101, A-5"
                    data-testid="lot-number-input"
                  />
                </div>
                <div>
                  <Label>Parcel ID</Label>
                  <Input
                    value={formData.parcel_id}
                    onChange={(e) => updateFormData("parcel_id", e.target.value)}
                    placeholder="County parcel ID"
                  />
                </div>
              </div>
              
              <div>
                <Label>Address *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => updateFormData("address", e.target.value)}
                  placeholder="Full street address"
                  data-testid="address-input"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Block</Label>
                  <Input
                    value={formData.block}
                    onChange={(e) => updateFormData("block", e.target.value)}
                    placeholder="e.g., Block A"
                  />
                </div>
                <div>
                  <Label>Phase</Label>
                  <Input
                    value={formData.phase}
                    onChange={(e) => updateFormData("phase", e.target.value)}
                    placeholder="e.g., Phase 1"
                  />
                </div>
                <div>
                  <Label>Acreage</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.acreage}
                    onChange={(e) => updateFormData("acreage", e.target.value)}
                    placeholder="e.g., 2.5"
                  />
                </div>
              </div>
              
              <div>
                <Label>Owner (Optional)</Label>
                <Select
                  value={formData.owner_id || "none"}
                  onValueChange={(v) => updateFormData("owner_id", v === "none" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an owner (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No owner assigned</SelectItem>
                    {owners.map((owner) => (
                      <SelectItem key={owner.owner_id} value={owner.owner_id}>
                        {owner.name || `${owner.first_name} ${owner.last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => updateFormData("notes", e.target.value)}
                  placeholder="Additional notes about this property..."
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Step 2: Review & Save */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-3">Review Property Details</h3>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Lot Number:</span>
                    <p className="font-medium">{formData.lot_number}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Address:</span>
                    <p className="font-medium">{formData.address}</p>
                  </div>
                  {formData.block && (
                    <div>
                      <span className="text-muted-foreground">Block:</span>
                      <p className="font-medium">{formData.block}</p>
                    </div>
                  )}
                  {formData.phase && (
                    <div>
                      <span className="text-muted-foreground">Phase:</span>
                      <p className="font-medium">{formData.phase}</p>
                    </div>
                  )}
                  {formData.acreage && (
                    <div>
                      <span className="text-muted-foreground">Acreage:</span>
                      <p className="font-medium">{formData.acreage} acres</p>
                    </div>
                  )}
                  {formData.parcel_id && (
                    <div>
                      <span className="text-muted-foreground">Parcel ID:</span>
                      <p className="font-medium">{formData.parcel_id}</p>
                    </div>
                  )}
                </div>
                
                {formData.notes && (
                  <div className="mt-3">
                    <span className="text-muted-foreground text-sm">Notes:</span>
                    <p className="text-sm">{formData.notes}</p>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> After creating the lot, you can add GIS mapping (lot boundary, building envelope, easements) from the property detail page.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <DialogFooter className="flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <Button variant="outline" onClick={goToPrevStep}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {currentStep < 2 ? (
              <Button onClick={goToNextStep}>
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving} data-testid="save-lot-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Lot
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddLotWizard;
